import { Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import {
  COLUMN_TYPE_FILES_ACTIONS,
  COLUMN_TYPE_FILES_CATEGORY,
  COLUMN_TYPE_FILES_DATE_AND_SOURCE,
  COLUMN_TYPE_FILES_FILE_NAME,
  COLUMN_TYPE_FILES_SHARED_WITH,
  COLUMN_TYPE_FILES_UNAVAILABLE_REASON,
  FILE_SHARE_MODE_NONE,
} from "core/consts";
import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import {
  formatUnixDate,
  getDateValue,
  getLocale,
} from "core/model/utils/dates";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { getUnixTime } from "date-fns";
import { Button } from "ds/ui";
import { LockSecurePin } from "ds_legacy/components/Icons/SecurePin";
import InfoChip from "ds_legacy/components/InfoChip";
import Tooltip from "ds_legacy/components/Tooltip";
import { ACCENT_DARK_COLOR, GREY_600 } from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { dp } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_12,
  FONT_SIZE_14,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_REGULAR,
  LINE_HEIGHT_18,
  LINE_HEIGHT_20,
} from "ds_legacy/materials/typography";
import { getSharedWith } from "dsl/ecosystems/PatientFiles/utils";
import { isFileHISStateful } from "dsl/organisms/FileTables";
import { blurStyle } from "dsl/organisms/PatientProfileDetailsShared/shared";
import {
  AlertTriangleIcon,
  CheckIcon,
  CircleAlertIcon,
  SendIcon,
  SparklesIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "translations";
import { CellProps, ValueWithToolTip, getDefaultValue } from ".";
import { DefaultCellBodyText } from "../styles";

export function CategoryValue({
  category,
}: CellProps<typeof COLUMN_TYPE_FILES_CATEGORY>) {
  const getOntology = useGetOntology();

  return (
    <InfoChip
      label={getOntology({
        key: category,
        type: ONTOLOGIES.FILE_CATEGORY,
        useShortValues: true,
      })}
      tooltip={getOntology({
        key: category,
        type: ONTOLOGIES.FILE_CATEGORY,
      })}
    />
  );
}

export function SharedWithValue({
  careseekerId,
  share_mode,
}: CellProps<typeof COLUMN_TYPE_FILES_SHARED_WITH>) {
  const [value, setValue] = useState<string>("");
  const translations = useTranslations();

  useEffect(() => {
    if (share_mode) {
      const sharedWith = getSharedWith({
        share_mode,
        translations,
      });
      setValue(sharedWith);
    }
  }, [share_mode, translations, careseekerId]);

  if (share_mode === FILE_SHARE_MODE_NONE) {
    return <InfoChip color="info" label={value} variant="outlined" />;
  }

  return <ValueWithToolTip value={value} />;
}

export function UnavailableReason({
  unavailable_reason,
}: CellProps<typeof COLUMN_TYPE_FILES_UNAVAILABLE_REASON>) {
  const getOntology = useGetOntology();

  return (
    <div className="flex w-full items-center gap-2">
      <Typography sx={{ flex: 1, fontSize: dp(14), lineHeight: dp(20) }}>
        {getOntology({ key: unavailable_reason, type: ONTOLOGIES.FILE_ERROR })}
      </Typography>
      <span className="h-4 w-4 text-sm leading-5">
        <AlertTriangleIcon size={16} style={{ color: ACCENT_DARK_COLOR }} />
      </span>
    </div>
  );
}

export function FileName({
  isFileDeleted,
  isNewVersionAvailable,
  shouldBlur,
  updatedAt,
  value,
}: CellProps<typeof COLUMN_TYPE_FILES_FILE_NAME>) {
  const translations = useTranslations();
  const locale = useLocale();
  const formattedValue = getDefaultValue(value);
  const theme = useTheme();

  return (
    <VerticalLayout width="100%">
      <div className="flex w-full items-center gap-2">
        <Tooltip title={formattedValue}>
          <DefaultCellBodyText style={{ ...(shouldBlur ? blurStyle : {}) }}>
            {formattedValue}
          </DefaultCellBodyText>
        </Tooltip>

        <span className="h-4 w-4 text-sm leading-5">
          <LockSecurePin style={{ color: GREY_600, fontSize: FONT_SIZE_14 }} />
        </span>
      </div>
      {(isNewVersionAvailable || isFileDeleted) && (
        <div className="flex w-full items-center gap-1">
          <span>
            <CircleAlertIcon
              size={16}
              style={{ color: theme.palette.error.dark }}
            />
          </span>
          <DefaultCellBodyText
            color={theme.palette.error.dark}
            fontSize={FONT_SIZE_12}
            lineHeight={LINE_HEIGHT_18}
          >
            {isNewVersionAvailable && updatedAt
              ? translations.fileSection.fileListComponent.newVersionAvailable({
                  date: formatUnixDate(getUnixTime(updatedAt * 1000), locale, {
                    withTime: true,
                  }),
                })
              : translations.fileSection.fileListComponent
                  .descriptionSourceDeleted}
          </DefaultCellBodyText>
        </div>
      )}
    </VerticalLayout>
  );
}
export function DateAndSource({
  source,
  uploadDate,
}: CellProps<typeof COLUMN_TYPE_FILES_DATE_AND_SOURCE>) {
  const locale = useLocale();
  const currentLocale = getLocale(locale);
  const formattedDate = getDateValue(uploadDate, currentLocale);

  return (
    <VerticalLayout overflow="visible" width="auto">
      <Body
        fontSize={FONT_SIZE_14}
        fontWeight={FONT_WEIGHT_REGULAR}
        lineHeight={LINE_HEIGHT_20}
        margin="0"
      >
        {formattedDate}
      </Body>
      <Body
        fontSize={FONT_SIZE_12}
        fontWeight={FONT_WEIGHT_MEDIUM}
        lineHeight={LINE_HEIGHT_18}
        margin="0"
      >
        {source}
      </Body>
    </VerticalLayout>
  );
}

export function FileActionsValue({
  file,
  isConsultation,
  onShareClick,
}: CellProps<typeof COLUMN_TYPE_FILES_ACTIONS>) {
  const translations = useTranslations();
  const { extractOcr, extractOcrStatus, utils } = useExtractDocs();

  const isExtractionProcessing = utils.isFileExtractionProcessing(file);
  const isExtractionFailed = utils.isFileExtractionFailed(file);
  const isExtracted = utils.isFileExtracted(file);

  const getExtractButtonValue = () => {
    if (isExtractionFailed) {
      return {
        icon: <AlertTriangleIcon />,
        isDisabled: true,
        isLoading: false,
        label: translations.extractDocs.extractBanner.extractionError,
      };
    }
    if (isExtractionProcessing) {
      return {
        icon: <SparklesIcon />,
        isDisabled: false,
        isLoading: true,
        label: translations.extractDocs.filesTabChanges.buttonExtracting,
      };
    }
    if (isExtracted) {
      return {
        icon: <CheckIcon />,
        isDisabled: true,
        isLoading: false,
        label: translations.extractDocs.filesTabChanges.buttonExtracted,
      };
    }
    return {
      icon: <SparklesIcon />,
      isDisabled:
        !extractOcrStatus.isInitialResponseReceived || isFileHISStateful(file),
      isLoading: false,
      label: translations.extractDocs.filesTabChanges.buttonExtract,
    };
  };

  const { icon, isDisabled, isLoading, label } = getExtractButtonValue();

  return (
    <div className="flex items-center gap-2">
      {!isConsultation && (
        <Button
          color="primary"
          id="share-file"
          size="sm"
          startContent={<SendIcon />}
          variant="ghost"
          onPress={onShareClick}
        >
          {translations.actions.shareFile}
        </Button>
      )}
      <Button
        color="primary"
        id="extract-file"
        // disable for his files if not extracted already
        isDisabled={isDisabled}
        isLoading={isLoading}
        size="sm"
        startContent={icon}
        onPress={() => {
          // dont do this for his files
          extractOcr.handleDownloadAndRunOcr({
            files: [file],
          });
        }}
      >
        {label}
      </Button>
    </div>
  );
}
